/**
 * Ted #26 (#987) — the pure half of "Confirm this reservation only /
 * Confirm all N pending".
 *
 * Ted Gualtier, 2026-09-15: *"reduce duplicate work, but never remove staff
 * control."* Everything here exists to make the REVIEW possible — turning a
 * schedule payload into rows a staffer can read at 6:45 with a wet dog on the
 * leash, and deciding which reservations belong in the prompt at all.
 *
 * Pure and dependency-free so both surfaces (the Bookings page and Front
 * Desk) group, label and word things identically — Principle 1, one truth —
 * and so the rules are unit-testable without a DOM.
 */
import type { AdminBooking } from '../../lib/api.js';

export interface PendingReservation {
  bookingId: string;
  customerId: string;
  dogNames: string[];
  /** The customer-facing service name, never the engine key. */
  serviceLabel: string;
  /** `bookings.date` as the server sent it (a UTC-midnight calendar day). */
  dateISO: string;
  /** "8:00 AM", "Drop-off 7:30 AM · Pick-up 5:00 PM", or null. */
  timeLabel: string | null;
  notes: string | null;
  totalCents: number;
  /**
   * Ted asked for add-ons in the review list. They are not on the schedule
   * payload today: `BookingAddOn` is an attribute with no admin write path
   * and HTHD has zero rows (Phase 1 ground truth §7), so nothing real would
   * render. The field is here, and the row renders it when non-empty, so the
   * service legs of #984 / #940 Part B drop straight in.
   */
  addOns: string[];
  /**
   * LG (board Contracts §3) — the STAFF-REVIEW hold on this row (an intact
   * pair sharing an area, a missing agreement), null when none. A held row
   * needs a manager's reason to confirm; the dialog says so on the row and
   * asks for the reason once for the whole batch.
   */
  reviewHoldReason?: string | null;
  /**
   * Ted 10-09 (#915) — the LAST calendar day the reservation covers
   * (YYYY-MM-DD): `end_date` for a stay, else the day itself. A reservation
   * whose last day is before today is a visit that already happened, and
   * the server never emails a confirmation about one.
   */
  lastDayISO?: string;
  /**
   * Sawyer #1296 — the server's "already in the owner's notes" fact for this
   * reservation's note (`booking_note_in_owner_notes`); absent / null when
   * unknown, which never prompts.
   */
  bookingNoteInOwnerNotes?: boolean | null;
}

/**
 * Ted 10-09 (#915) — is this reservation for a visit that is already over?
 * `today` is the facility's YYYY-MM-DD. Mirrors the server's `isVisitOver`
 * (booking/confirmation-notifications.ts): today's visit is NOT over.
 */
export function isPastReservation(
  item: Pick<PendingReservation, 'dateISO' | 'lastDayISO'>,
  today: string,
): boolean {
  const last = (item.lastDayISO ?? item.dateISO).slice(0, 10);
  return last < today;
}

/** LG — is this row waiting on a manager? The server's `review_held` is the one definition; the reason alone is history once cleared. */
export function isHeldForReview(booking: Pick<AdminBooking, 'review_held' | 'review_hold_reason' | 'review_cleared_at'>): boolean {
  if (typeof booking.review_held === 'boolean') return booking.review_held;
  return Boolean(booking.review_hold_reason) && !booking.review_cleared_at;
}

/** "Hazel", "Hazel & Hank", "Hazel, Hank & Skipper". */
export function joinDogNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** "7:30" / "07:30" → "7:30 AM". Anything unparseable passes through. */
export function formatClock(hhmm: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm.trim());
  if (!match) return hhmm;
  const hours = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours) || hours < 0 || hours > 23) return hhmm;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${minutes} ${suffix}`;
}

/**
 * "Mon, Sep 22". Formatted in UTC ON PURPOSE: `bookings.date` is a Postgres
 * DATE and reads back as UTC midnight, so formatting it in the browser's zone
 * shows a Denver staffer the day BEFORE for every reservation.
 */
export function formatReservationDate(dateISO: string): string {
  const d = new Date(dateISO);
  if (Number.isNaN(d.getTime())) return dateISO.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The time facts the desk needs to spot an unusual reservation. A booking
 * with a customer-requested window shows the window (that IS the difference
 * worth seeing); otherwise the scheduled start; otherwise nothing — never an
 * invented "all day".
 */
export function reservationTimeLabel(booking: AdminBooking): string | null {
  const dropOff = booking.drop_off_time?.trim();
  const pickUp = booking.pick_up_time?.trim();
  if (dropOff || pickUp) {
    const parts: string[] = [];
    if (dropOff) parts.push(`Drop-off ${formatClock(dropOff)}`);
    if (pickUp) parts.push(`Pick-up ${formatClock(pickUp)}`);
    return parts.join(' · ');
  }
  const start = booking.start_time?.trim();
  return start ? formatClock(start) : null;
}

export function toPendingReservation(
  booking: AdminBooking,
  serviceLabel: string,
): PendingReservation {
  const notes = booking.notes?.trim();
  return {
    bookingId: booking.id,
    customerId: booking.customer_id,
    dogNames:
      booking.dog_names && booking.dog_names.length > 0
        ? booking.dog_names
        : (booking.dogs ?? []).map((d) => d.name).filter(Boolean),
    serviceLabel,
    dateISO: booking.date,
    timeLabel: reservationTimeLabel(booking),
    notes: notes && notes.length > 0 ? notes : null,
    totalCents: booking.total_cents,
    addOns: [],
    reviewHoldReason: isHeldForReview(booking) ? (booking.review_hold_reason ?? null) : null,
    lastDayISO: (booking.end_date ?? booking.start_date ?? booking.date).slice(0, 10),
    bookingNoteInOwnerNotes: booking.booking_note_in_owner_notes ?? null,
  };
}

/**
 * Every reservation the prompt may offer: the clicked one plus the rest of
 * the HOUSEHOLD's pending reservations, oldest first.
 *
 * Grouping is by `customer_id` ONLY — the same rule the household check-in
 * card (#954) settled on. Two "J. Smith"s are not one household, and a batch
 * that confirmed a stranger's reservation because the names matched would be
 * the exact opposite of "never remove staff control".
 *
 * The anchor is always included even if the wider fetch missed it (it can sit
 * outside the lookup window), and it is never duplicated.
 */
export function pendingForHousehold(
  anchor: AdminBooking,
  candidates: readonly AdminBooking[],
): AdminBooking[] {
  const rows = candidates.filter(
    (b) =>
      b.id !== anchor.id &&
      b.status === 'pending' &&
      b.customer_id === anchor.customer_id,
  );
  return [anchor, ...rows].sort(compareByWhen);
}

/** Oldest first — the approval queue's order (#923). Ties break on id. */
export function compareByWhen(a: AdminBooking, b: AdminBooking): number {
  const byDate = a.date.localeCompare(b.date);
  if (byDate !== 0) return byDate;
  const byTime = (a.start_time ?? '').localeCompare(b.start_time ?? '');
  if (byTime !== 0) return byTime;
  return a.id.localeCompare(b.id);
}

/** Oldest-first ordering for the pending queue view. */
export function sortOldestFirst(bookings: readonly AdminBooking[]): AdminBooking[] {
  return [...bookings].sort(compareByWhen);
}

/**
 * What a Bookings row says about WHEN it is, given which list it is in.
 *
 * The day view is filtered to one date the staffer picked, so the date is
 * already on screen in the picker and repeating it on every row is noise —
 * the row leads with the clock time.
 *
 * The approval queue is not: it spans -180/+365 days (`pendingWindow`) and is
 * sorted oldest first, so HTHD's five never-actioned reservations and any
 * future one land in the same list. A row that renders only "08:00" is then
 * indistinguishable from another day's 08:00 — the staffer taps Confirm on
 * whichever is nearest the thumb. The queue is the ONE view where the date is
 * the load-bearing fact, so it leads.
 *
 * Returns null when the row has neither (never an invented "all day").
 */
export function bookingWhenLabel(
  booking: Pick<AdminBooking, 'date' | 'start_time'>,
  opts: { withDate: boolean },
): string | null {
  const time = booking.start_time?.trim() || null;
  if (!opts.withDate) return time;
  const day = formatReservationDate(booking.date);
  return time ? `${day} · ${time}` : day;
}

/**
 * What the outcome banner says AND how loudly it says it. Tone travels with
 * the text on purpose: the first cut of this shipped a plain string and every
 * caller rendered it `ferro-banner--success`, so "Nothing was confirmed." and
 * "Couldn't confirm …" both came out green with `role="status"`. At 6:45 with
 * a line at the door green reads as *done*: the staffer glances, walks away,
 * and the reservation that was left pending is never revisited. Making the
 * tone part of the value is what stops a caller choosing it wrong again.
 */
export type ConfirmBannerTone = 'success' | 'warning';

export interface ConfirmOutcomeBanner {
  text: string;
  tone: ConfirmBannerTone;
  /**
   * `alert` only when NOTHING moved — the case where the staffer's whole
   * action came to nothing and a glance would otherwise miss it. A partial
   * success is a warning the eye can find (amber, and it names the refusals),
   * but it is not worth interrupting a screen reader mid-sentence for.
   */
  role: 'status' | 'alert';
}

/**
 * The sentence on the banner after a batch settles. States what HAPPENED
 * (Principle 5: the outcome lives somewhere inspectable, not only in a
 * toast) and names the refusals so the desk knows what is still waiting.
 *
 * Three outcomes, three tones:
 *   - everything confirmed          → success · status
 *   - some confirmed, some refused  → warning · status  (the refusals are named)
 *   - nothing confirmed             → warning · alert
 */
export function summarizeConfirmOutcome(args: {
  confirmedCount: number;
  refusals: ReadonlyArray<{ label: string; reason: string }>;
}): ConfirmOutcomeBanner {
  const { confirmedCount, refusals } = args;
  const confirmed =
    confirmedCount === 0
      ? 'Nothing was confirmed'
      : `Confirmed ${confirmedCount} reservation${confirmedCount === 1 ? '' : 's'}`;
  const text =
    refusals.length === 0
      ? `${confirmed}.`
      : `${confirmed}. Left as-is: ${refusals
          .map((r) => `${r.label} — ${r.reason}`)
          .join('; ')}.`;
  if (confirmedCount === 0) return { text, tone: 'warning', role: 'alert' };
  if (refusals.length > 0) return { text, tone: 'warning', role: 'status' };
  return { text, tone: 'success', role: 'status' };
}

/**
 * The one-tap confirm threw — a network error, a 403, a row that moved under
 * the staffer. Nothing was confirmed, so this is the loudest of the three:
 * amber, and announced.
 */
export function confirmFailureBanner(
  label: string,
  reason: string,
): ConfirmOutcomeBanner {
  return {
    text: `Couldn't confirm ${label} — ${reason}`,
    tone: 'warning',
    role: 'alert',
  };
}

/** "Druzy · Mon, Sep 22" — how a refused reservation is named on the banner. */
export function reservationLabel(item: PendingReservation): string {
  const dogs = joinDogNames(item.dogNames);
  const when = formatReservationDate(item.dateISO);
  return dogs ? `${dogs} · ${when}` : when;
}
